import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface SredEligibility {
  overall_eligible: boolean;
  confidence_percentage: number;
  eligible_hours_estimate: number;
  eligible_percentage: number;
  eligibility_level: string;
}

interface TechnologicalUncertainty {
  description: string;
  technical_context?: string;
  resolution_approach: string;
  related_activities: string[];
}

interface TechnologicalAdvancement {
  description: string;
  evidence: string;
  impact: string;
}

interface HypothesisTested {
  hypothesis: string;
  test_methodology: string;
  outcome: string;
  iterations?: number;
}

interface TechnicalResult {
  description: string;
  metrics?: string;
  evidence: string;
  impact: string;
}

interface ActivityClassification {
  activity: string;
  sred_category?: string;
  category?: string;
  technical_complexity?: 'high' | 'medium' | 'low';
  eligible_percentage: number;
  justification: string;
}

interface ClaimRecommendations {
  recommended_claim_hours: number;
  recommended_claim_percentage: number;
  key_points_for_claim?: string[];
  key_technical_points?: string[];
  risks?: string[];
  documentation_gaps?: string[];
  mitigation_strategies?: string[];
  evidence_strength?: 'strong' | 'moderate' | 'weak';
}

interface CustomModule {
  name: string;
  description: string;
  files: string[];
}

interface TechnologyStack {
  content: string;
  languages: string[];
  frameworks: string[];
  libraries: string[];
  custom_modules: CustomModule[];
  architectural_patterns: string[];
  notable_customizations: string[];
}

interface ProjectAnalysis {
  project_id: number;
  project_name: string;
  client_name: string | null;
  total_hours: number;
  total_entries: number;
  employee_hours: number;
  contractor_hours: number;
  employees: string[];
  contractors: string[];
  date_range: { start: string; end: string };
  activities_summary: Array<{
    task: string;
    hours: number;
    entries_count: number;
    descriptions: string[];
    users: string[];
  }>;
  ai_analysis: {
    sred_eligibility: SredEligibility;
    project_technical_summary?: {
      content: string;
      word_count: number;
    };
    technology_stack?: TechnologyStack;
    t661_form_content?: {
      line_242: { title: string; content: string; word_count: number };
      line_244: { title: string; content: string; word_count: number };
      line_246: { title: string; content: string; word_count: number };
    };
    technological_uncertainties?: TechnologicalUncertainty[];
    technological_advancements?: TechnologicalAdvancement[];
    hypotheses_tested?: HypothesisTested[];
    technical_results?: TechnicalResult[];
    systematic_investigation?: {
      present: boolean;
      methodology?: string;
      evidence: string;
      improvements_needed: string[];
    };
    documentation_quality?: {
      score: number;
      strengths: string[];
      weaknesses: string[];
      recommendations?: string[];
    };
    activity_classification?: ActivityClassification[];
    claim_recommendations: ClaimRecommendations;
    executive_summary: string;
  } | null;
  error?: string;
}

interface ClientSummary {
  total_projects: number;
  total_hours: number;
  employee_hours: number;
  contractor_hours: number;
  estimated_eligible_hours: number;
  estimated_eligible_percentage: number;
}

interface ClientAnalysis {
  client_id: number | null;
  client_name: string;
  projects: ProjectAnalysis[];
  client_summary: ClientSummary | null;
}

interface OverallSummary {
  total_projects_analyzed: number;
  total_hours: number;
  employee_hours: number;
  contractor_hours: number;
  estimated_eligible_hours: number;
  estimated_eligible_percentage: number;
  projects_with_high_eligibility: number;
  projects_with_medium_eligibility: number;
  projects_with_low_eligibility: number;
}

interface SredReportData {
  generated_at: string;
  organization_id: number;
  organization_name: string;
  clients: ClientAnalysis[];
  overall_summary: OverallSummary | null;
}

interface TranslationFn {
  (key: string): string;
}

// Color definitions
const colors = {
  primary: [59, 130, 246] as [number, number, number],      // blue-500
  success: [34, 197, 94] as [number, number, number],       // green-500
  warning: [234, 179, 8] as [number, number, number],       // yellow-500
  danger: [239, 68, 68] as [number, number, number],        // red-500
  purple: [168, 85, 247] as [number, number, number],       // purple-500
  indigo: [99, 102, 241] as [number, number, number],       // indigo-500
  amber: [245, 158, 11] as [number, number, number],        // amber-500
  gray: [107, 114, 128] as [number, number, number],        // gray-500
  darkGray: [55, 65, 81] as [number, number, number],       // gray-700
  lightGray: [229, 231, 235] as [number, number, number],   // gray-200
};

// Helper to safely convert to number and format
const toNum = (value: any): number => Number(value || 0);
const formatNum = (value: any, decimals: number = 1): string => toNum(value).toFixed(decimals);

export const generateSredPdf = (
  reportData: SredReportData,
  t: TranslationFn,
  filters: { start_date: string; end_date: string }
): void => {
  const doc = new jsPDF();
  let yPos = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);

  // Helper to check if we need a new page
  const checkNewPage = (requiredSpace: number): void => {
    if (yPos + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Helper to draw section header
  const drawSectionHeader = (text: string, bgColor: [number, number, number] = colors.primary): void => {
    checkNewPage(15);
    doc.setFillColor(...bgColor);
    doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(text, margin + 3, yPos + 2);
    doc.setTextColor(0, 0, 0);
    yPos += 12;
  };

  // Helper to draw a stat box
  const drawStatBox = (
    x: number,
    y: number,
    width: number,
    label: string,
    value: string,
    bgColor: [number, number, number]
  ): void => {
    doc.setFillColor(...bgColor);
    doc.roundedRect(x, y, width, 20, 2, 2, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text(value, x + width / 2, y + 9, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.gray);
    doc.text(label, x + width / 2, y + 16, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  // ============ TITLE PAGE ============
  // Header
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 50, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(t('sred.pdfTitle') || 'SR&ED Tax Credit Eligibility Report', pageWidth / 2, 25, { align: 'center' });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(reportData.organization_name, pageWidth / 2, 38, { align: 'center' });

  doc.setTextColor(0, 0, 0);
  yPos = 60;

  // Report info
  doc.setFontSize(10);
  doc.setTextColor(...colors.gray);
  doc.text(`${t('sred.generatedAt') || 'Generated'}: ${new Date(reportData.generated_at).toLocaleString()}`, margin, yPos);
  doc.text(`${t('sred.period') || 'Period'}: ${filters.start_date} - ${filters.end_date}`, pageWidth - margin, yPos, { align: 'right' });
  yPos += 15;

  // ============ OVERALL SUMMARY ============
  if (reportData.overall_summary) {
    drawSectionHeader(t('sred.overallSummary') || 'Overall Summary');

    const summary = reportData.overall_summary;
    const boxWidth = (contentWidth - 12) / 5;
    const startX = margin;

    // First row of stats
    drawStatBox(startX, yPos, boxWidth, t('sred.projectsAnalyzed') || 'Projects', toNum(summary.total_projects_analyzed).toString(), colors.lightGray);
    drawStatBox(startX + boxWidth + 3, yPos, boxWidth, t('sred.totalHours') || 'Total Hours', formatNum(summary.total_hours), colors.lightGray);
    drawStatBox(startX + (boxWidth + 3) * 2, yPos, boxWidth, t('sred.eligibleHours') || 'Eligible Hours', formatNum(summary.estimated_eligible_hours), [187, 247, 208] as [number, number, number]);
    drawStatBox(startX + (boxWidth + 3) * 3, yPos, boxWidth, t('sred.eligiblePercentage') || 'Eligible %', `${formatNum(summary.estimated_eligible_percentage)}%`, [191, 219, 254] as [number, number, number]);
    drawStatBox(startX + (boxWidth + 3) * 4, yPos, boxWidth, t('sred.clients') || 'Clients', (reportData.clients?.length || 0).toString(), [233, 213, 255] as [number, number, number]);

    yPos += 28;

    // Second row - Employee/Contractor breakdown
    const halfWidth = (contentWidth - 6) / 4;
    drawStatBox(startX, yPos, halfWidth, t('sred.employeeHours') || 'Employee Hours', formatNum(summary.employee_hours), [199, 210, 254] as [number, number, number]);
    drawStatBox(startX + halfWidth + 3, yPos, halfWidth, t('sred.contractorHours') || 'Contractor Hours', formatNum(summary.contractor_hours), [254, 243, 199] as [number, number, number]);
    drawStatBox(startX + (halfWidth + 3) * 2, yPos, halfWidth, t('sred.highEligibility') || 'High Eligibility', toNum(summary.projects_with_high_eligibility).toString(), [187, 247, 208] as [number, number, number]);
    drawStatBox(startX + (halfWidth + 3) * 3, yPos, halfWidth, t('sred.otherEligibility') || 'Other', (toNum(summary.projects_with_medium_eligibility) + toNum(summary.projects_with_low_eligibility)).toString(), [254, 249, 195] as [number, number, number]);

    yPos += 30;
  }

  // ============ CLIENT DETAILS ============
  reportData.clients?.forEach((client) => {
    checkNewPage(50);

    // Client header
    drawSectionHeader(client.client_name, [71, 85, 105] as [number, number, number]);

    if (client.client_summary) {
      doc.setFontSize(9);
      doc.setTextColor(...colors.gray);
      const summaryText = `${client.projects.length} ${t('sred.projects') || 'projects'} | ${formatNum(client.client_summary.total_hours)} ${t('sred.hours') || 'hours'} | ${formatNum(client.client_summary.employee_hours)}h ${t('sred.employees') || 'employees'} | ${formatNum(client.client_summary.contractor_hours)}h ${t('sred.contractors') || 'contractors'} | ${formatNum(client.client_summary.estimated_eligible_percentage, 0)}% ${t('sred.eligible') || 'eligible'}`;
      doc.text(summaryText, margin, yPos);
      yPos += 10;
    }

    // Project details
    client.projects.forEach((project) => {
      checkNewPage(80);

      // Project header
      doc.setFillColor(249, 250, 251);
      doc.rect(margin, yPos - 3, contentWidth, 14, 'F');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.darkGray);
      doc.text(project.project_name, margin + 3, yPos + 4);

      // Eligibility badge
      if (project.ai_analysis?.sred_eligibility) {
        const level = project.ai_analysis.sred_eligibility.eligibility_level;
        const badgeColor = level === 'high' ? colors.success : level === 'medium' ? colors.warning : colors.danger;
        const badgeText = `${level.toUpperCase()} (${project.ai_analysis.sred_eligibility.eligible_percentage}%)`;
        const badgeWidth = doc.getTextWidth(badgeText) + 8;

        doc.setFillColor(...badgeColor);
        doc.roundedRect(pageWidth - margin - badgeWidth - 3, yPos - 1, badgeWidth, 8, 1, 1, 'F');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text(badgeText, pageWidth - margin - badgeWidth / 2 - 3, yPos + 4, { align: 'center' });
      }

      yPos += 14;

      // Project stats
      doc.setFontSize(8);
      doc.setTextColor(...colors.gray);
      doc.setFont('helvetica', 'normal');
      doc.text(`${formatNum(project.total_hours)} ${t('sred.hours') || 'hours'} | ${project.total_entries} ${t('sred.entries') || 'entries'} | ${formatNum(project.employee_hours)}h ${t('sred.emp') || 'emp'} / ${formatNum(project.contractor_hours)}h ${t('sred.ctr') || 'ctr'}`, margin + 3, yPos);
      yPos += 8;

      if (project.ai_analysis) {
        // Project Technical Summary (new format)
        if (project.ai_analysis.project_technical_summary?.content) {
          checkNewPage(40);
          doc.setFillColor(238, 242, 255);
          doc.rect(margin, yPos - 2, contentWidth, 12, 'F');
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.indigo);
          doc.text(t('sred.technicalSummary') || 'Project Technical Summary', margin + 3, yPos + 6);
          yPos += 14;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.darkGray);
          const techSummaryLines = doc.splitTextToSize(project.ai_analysis.project_technical_summary.content, contentWidth - 6);
          doc.setFontSize(9);
          techSummaryLines.forEach((line: string) => {
            checkNewPage(5);
            doc.text(line, margin + 3, yPos);
            yPos += 4.5;
          });
          yPos += 8;
        }

        // Technology Stack Section
        if (project.ai_analysis.technology_stack?.content) {
          checkNewPage(50);
          doc.setFillColor(243, 232, 255); // purple-100
          doc.rect(margin, yPos - 2, contentWidth, 12, 'F');
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.purple);
          doc.text(t('sred.technologyStack') || 'Technology Stack & Customizations', margin + 3, yPos + 6);
          yPos += 14;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.darkGray);
          const techStackLines = doc.splitTextToSize(project.ai_analysis.technology_stack.content, contentWidth - 6);
          doc.setFontSize(9);
          techStackLines.forEach((line: string) => {
            checkNewPage(5);
            doc.text(line, margin + 3, yPos);
            yPos += 4.5;
          });
          yPos += 5;

          // Languages & Frameworks inline
          const techDetails: string[] = [];
          if ((project.ai_analysis.technology_stack.languages ?? []).length > 0) {
            techDetails.push(`${t('sred.languages') || 'Languages'}: ${project.ai_analysis.technology_stack.languages.join(', ')}`);
          }
          if ((project.ai_analysis.technology_stack.frameworks ?? []).length > 0) {
            techDetails.push(`${t('sred.frameworks') || 'Frameworks'}: ${project.ai_analysis.technology_stack.frameworks.join(', ')}`);
          }
          if (techDetails.length > 0) {
            doc.setFontSize(8);
            doc.setTextColor(...colors.purple);
            doc.setFont('helvetica', 'bold');
            techDetails.forEach(detail => {
              checkNewPage(5);
              doc.text(detail, margin + 3, yPos);
              yPos += 4;
            });
          }

          // Custom Modules
          if ((project.ai_analysis.technology_stack.custom_modules ?? []).length > 0) {
            yPos += 3;
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.purple);
            doc.text(t('sred.customModules') || 'Custom Modules:', margin + 3, yPos);
            yPos += 4;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.darkGray);
            (project.ai_analysis.technology_stack.custom_modules ?? []).forEach(mod => {
              checkNewPage(8);
              doc.text(`• ${mod.name}: ${mod.description}`, margin + 6, yPos);
              yPos += 4;
            });
          }

          yPos += 8;
        }
        // Executive Summary (fallback for old format)
        else if (project.ai_analysis.executive_summary) {
          checkNewPage(30);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.primary);
          doc.text(t('sred.executiveSummary') || 'Executive Summary', margin + 3, yPos);
          yPos += 5;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.darkGray);
          const summaryLines = doc.splitTextToSize(project.ai_analysis.executive_summary, contentWidth - 6);
          doc.setFontSize(8);
          summaryLines.forEach((line: string) => {
            checkNewPage(6);
            doc.text(line, margin + 3, yPos);
            yPos += 4;
          });
          yPos += 5;
        }

        // Eligibility Details Table
        checkNewPage(30);
        const eligibilityRows: string[][] = [
          [t('sred.confidence') || 'Confidence', `${project.ai_analysis.sred_eligibility.confidence_percentage}%`],
          [t('sred.eligibleHours') || 'Eligible Hours', `${formatNum(project.ai_analysis.sred_eligibility.eligible_hours_estimate)}h`],
          [t('sred.eligiblePercentage') || 'Eligible Percentage', `${project.ai_analysis.sred_eligibility.eligible_percentage}%`],
        ];
        if (project.ai_analysis.documentation_quality?.score !== undefined) {
          eligibilityRows.push([t('sred.documentationQuality') || 'Documentation Score', `${project.ai_analysis.documentation_quality.score}/100`]);
        }
        autoTable(doc, {
          startY: yPos,
          head: [[t('sred.eligibilityDetails') || 'Eligibility Details', '']],
          body: eligibilityRows,
          theme: 'grid',
          headStyles: { fillColor: colors.primary, fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40, halign: 'right' } },
          margin: { left: margin + 3, right: margin + 3 },
          tableWidth: 100,
        });
        yPos = (doc as any).lastAutoTable.finalY + 5;

        // Technological Uncertainties
        if ((project.ai_analysis.technological_uncertainties ?? []).length > 0) {
          checkNewPage(30);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.purple);
          doc.text(t('sred.technologicalUncertainties') || 'Technological Uncertainties', margin + 3, yPos);
          yPos += 5;

          (project.ai_analysis.technological_uncertainties ?? []).forEach((uncertainty, idx) => {
            checkNewPage(15);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.darkGray);
            doc.text(`${idx + 1}. ${uncertainty.description}`, margin + 6, yPos);
            yPos += 4;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.gray);
            const resolutionLines = doc.splitTextToSize(`${t('sred.resolution') || 'Resolution'}: ${uncertainty.resolution_approach}`, contentWidth - 12);
            resolutionLines.forEach((line: string) => {
              checkNewPage(5);
              doc.text(line, margin + 9, yPos);
              yPos += 4;
            });
            yPos += 2;
          });
          yPos += 3;
        }

        // Technological Advancements
        if ((project.ai_analysis.technological_advancements ?? []).length > 0) {
          checkNewPage(30);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...colors.success);
          doc.text(t('sred.technologicalAdvancements') || 'Technological Advancements', margin + 3, yPos);
          yPos += 5;

          (project.ai_analysis.technological_advancements ?? []).forEach((advancement, idx) => {
            checkNewPage(15);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.darkGray);
            doc.text(`${idx + 1}. ${advancement.description}`, margin + 6, yPos);
            yPos += 4;
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.gray);
            const evidenceLines = doc.splitTextToSize(advancement.evidence, contentWidth - 12);
            evidenceLines.forEach((line: string) => {
              checkNewPage(5);
              doc.text(line, margin + 9, yPos);
              yPos += 4;
            });
            yPos += 2;
          });
          yPos += 3;
        }

        // Activity Classification Table
        if ((project.ai_analysis.activity_classification ?? []).length > 0) {
          checkNewPage(40);
          autoTable(doc, {
            startY: yPos,
            head: [[
              t('sred.activity') || 'Activity',
              t('sred.category') || 'Category',
              t('sred.eligible') || 'Eligible %',
              t('sred.justification') || 'Justification'
            ]],
            body: (project.ai_analysis.activity_classification ?? []).map(act => [
              act.activity,
              (act.sred_category || act.category || '').replace('_', ' '),
              `${act.eligible_percentage}%`,
              act.justification
            ]),
            theme: 'striped',
            headStyles: { fillColor: colors.indigo, fontSize: 7 },
            bodyStyles: { fontSize: 7 },
            columnStyles: {
              0: { cellWidth: 35 },
              1: { cellWidth: 30 },
              2: { cellWidth: 20, halign: 'center' },
              3: { cellWidth: 'auto' }
            },
            margin: { left: margin + 3, right: margin + 3 },
          });
          yPos = (doc as any).lastAutoTable.finalY + 5;
        }

        // Claim Recommendations
        if (project.ai_analysis.claim_recommendations) {
          checkNewPage(40);
          doc.setFillColor(236, 253, 245);
          doc.rect(margin + 3, yPos - 2, contentWidth - 6, 20, 'F');

          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(22, 101, 52);
          doc.text(t('sred.claimRecommendations') || 'Claim Recommendations', margin + 6, yPos + 4);

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(`${t('sred.recommendedHours') || 'Recommended Hours'}: ${formatNum(project.ai_analysis.claim_recommendations.recommended_claim_hours)}h`, margin + 6, yPos + 11);
          doc.text(`${t('sred.recommendedPercentage') || 'Recommended %'}: ${project.ai_analysis.claim_recommendations.recommended_claim_percentage}%`, margin + 80, yPos + 11);

          yPos += 25;

          // Key points
          const keyPoints = project.ai_analysis.claim_recommendations.key_points_for_claim || project.ai_analysis.claim_recommendations.key_technical_points || [];
          if (keyPoints.length > 0) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.success);
            doc.text(t('sred.keyPoints') || 'Key Points:', margin + 6, yPos);
            yPos += 4;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.darkGray);
            keyPoints.forEach(point => {
              checkNewPage(6);
              const pointLines = doc.splitTextToSize(`• ${point}`, contentWidth - 15);
              pointLines.forEach((line: string) => {
                doc.text(line, margin + 9, yPos);
                yPos += 4;
              });
            });
            yPos += 3;
          }

          // Risks / Documentation Gaps
          const risks = project.ai_analysis.claim_recommendations.risks || project.ai_analysis.claim_recommendations.documentation_gaps || [];
          if (risks.length > 0) {
            checkNewPage(15);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.danger);
            doc.text(project.ai_analysis.claim_recommendations.documentation_gaps ? (t('sred.documentationGaps') || 'Documentation Gaps:') : (t('sred.risks') || 'Risks:'), margin + 6, yPos);
            yPos += 4;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...colors.darkGray);
            risks.forEach(risk => {
              checkNewPage(6);
              const riskLines = doc.splitTextToSize(`• ${risk}`, contentWidth - 15);
              riskLines.forEach((line: string) => {
                doc.text(line, margin + 9, yPos);
                yPos += 4;
              });
            });
            yPos += 3;
          }
        }
      }

      yPos += 10;
    });

    yPos += 5;
  });

  // Footer on last page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...colors.gray);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    doc.text(reportData.organization_name, margin, pageHeight - 10);
    doc.text(new Date().toLocaleDateString(), pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // Save the PDF
  const filename = `SRED_Report_${reportData.organization_name.replace(/[^a-z0-9]/gi, '_')}_${filters.start_date}_${filters.end_date}.pdf`;
  doc.save(filename);
};

/**
 * Generate a simplified PDF for sharing with SR&ED accounting firms
 * Clean format: Client, Project, Hours, Technical Description
 */
export const generateSredSimplePdf = (
  reportData: SredReportData,
  t: TranslationFn,
  filters: { start_date: string; end_date: string }
): void => {
  const doc = new jsPDF();
  let yPos = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);

  const checkNewPage = (requiredSpace: number): void => {
    if (yPos + requiredSpace > pageHeight - 25) {
      doc.addPage();
      yPos = 25;
    }
  };

  // === HEADER ===
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59); // slate-800
  doc.text(reportData.organization_name, margin, yPos);
  yPos += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105); // slate-500
  doc.text(`${t('sred.period') || 'Period'}: ${filters.start_date} - ${filters.end_date}`, margin, yPos);
  yPos += 6;
  doc.text(`${t('sred.generatedAt') || 'Generated'}: ${new Date().toLocaleDateString()}`, margin, yPos);
  yPos += 15;

  // === SUMMARY TABLE ===
  if (reportData.overall_summary) {
    autoTable(doc, {
      startY: yPos,
      head: [[
        t('sred.totalHours') || 'Total Hours',
        t('sred.employeeHours') || 'Employee Hours',
        t('sred.contractorHours') || 'Contractor Hours',
        t('sred.eligibleHours') || 'Eligible Hours'
      ]],
      body: [[
        formatNum(reportData.overall_summary.total_hours),
        formatNum(reportData.overall_summary.employee_hours),
        formatNum(reportData.overall_summary.contractor_hours),
        formatNum(reportData.overall_summary.estimated_eligible_hours)
      ]],
      theme: 'grid',
      headStyles: { fillColor: [51, 65, 85], fontSize: 9, fontStyle: 'bold' }, // slate-700
      bodyStyles: { fontSize: 10, halign: 'center' },
      margin: { left: margin, right: margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // === PROJECTS BY CLIENT ===
  reportData.clients.forEach((client) => {
    client.projects.forEach((project) => {
      checkNewPage(80);

      // Client & Project Header
      doc.setFillColor(241, 245, 249); // slate-100
      doc.rect(margin, yPos - 3, contentWidth, 18, 'F');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59); // slate-800
      doc.text(client.client_name || t('sred.internalProject') || 'Internal Project', margin + 3, yPos + 4);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105); // slate-500
      doc.text(project.project_name, margin + 3, yPos + 11);

      yPos += 20;

      // Hours Info
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139); // slate-500
      const hoursText = `${formatNum(project.total_hours)}h total | ${formatNum(project.employee_hours)}h ${t('sred.employees') || 'employees'} | ${formatNum(project.contractor_hours)}h ${t('sred.contractors') || 'contractors'}`;
      doc.text(hoursText, margin + 3, yPos);
      yPos += 8;

      // Technical Description
      const description = project.ai_analysis?.project_technical_summary?.content
        || project.ai_analysis?.executive_summary
        || '';

      if (description) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(55, 65, 81); // gray-700

        const lines = doc.splitTextToSize(description, contentWidth - 6);
        lines.forEach((line: string) => {
          checkNewPage(5);
          doc.text(line, margin + 3, yPos);
          yPos += 4.5;
        });
      }

      // Technology Stack (simplified for firm export)
      if (project.ai_analysis?.technology_stack?.content) {
        yPos += 5;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(107, 33, 168); // purple-700
        doc.text(t('sred.technologyStack') || 'Technology Stack & Customizations', margin + 3, yPos);
        yPos += 5;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(55, 65, 81); // gray-700
        const techLines = doc.splitTextToSize(project.ai_analysis.technology_stack.content, contentWidth - 6);
        techLines.forEach((line: string) => {
          checkNewPage(5);
          doc.text(line, margin + 3, yPos);
          yPos += 4.5;
        });
      }

      yPos += 12;
    });
  });

  // === FOOTER ON ALL PAGES ===
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text(`Page ${i}/${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Confidential notice
    doc.setFontSize(7);
    doc.text(t('sred.confidential') || 'Confidential - For SR&ED purposes only', margin, pageHeight - 10);
  }

  // Save
  const filename = `SRED_${reportData.organization_name.replace(/[^a-z0-9]/gi, '_')}_${filters.start_date}_${filters.end_date}.pdf`;
  doc.save(filename);
};
